import { useCallback, useEffect } from "react";
import Button, { IconButton } from "domains/ui/components/Button";
import Icon, { IconId } from "domains/ui/components/Icon";
import Modal, { ModalBody, ModalContent } from "domains/ui/components/Modal";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";

import {
  Box,
  Center,
  Flex,
  HStack,
  Image,
  ImageProps,
  Text,
  VStack,
} from "@chakra-ui/react";

type BulletPoint = {
  icon: IconId;
  size?: string;
  text: string;
};

const MODAL_SIGNUP_CONTENT: Record<
  string,
  {
    illustration: ImageProps["src"];
    description: string;
    bulletPoints: BulletPoint[];
  }
> = {
  train: {
    illustration: "/signup-popup/train.jpg",
    description:
      "Create tailored models that match your visual identity, from scenes to characters.",
    bulletPoints: [
      {
        icon: "Nav/Model/Outline",
        size: "16px",
        text: "Train models with your own data",
      },
      {
        icon: "Ui/Search",
        text: "On-brand assets with full consistency",
      },
    ],
  },
  generate: {
    illustration: "/signup-popup/generate.jpg",
    description:
      "Create production-ready visual assets, with precision and efficiency.",
    bulletPoints: [
      {
        icon: "Nav/Model/Outline",
        size: "16px",
        text: "Unlock smart AI workflows",
      },
      {
        icon: "Ui/Search",
        text: "Tap into hundreds of models",
      },
    ],
  },
  edit: {
    illustration: "/signup-popup/edit.jpg",
    description:
      "Retouch, enhance, or transform your visuals using intelligent, context-aware models.",
    bulletPoints: [
      {
        icon: "Nav/Model/Outline",
        size: "16px",
        text: "From automated to manual edits",
      },
      {
        icon: "Ui/Search",
        text: "Intuitive tools built for control and quality",
      },
    ],
  },
};

export interface ModalSignUpProps {
  variant: keyof typeof MODAL_SIGNUP_CONTENT | undefined;
  onClose: () => void;
}

export default function ModalSignUp({ variant, onClose }: ModalSignUpProps) {
  const illustrationSrc = variant && MODAL_SIGNUP_CONTENT[variant].illustration;
  const description = variant && MODAL_SIGNUP_CONTENT[variant].description;
  const bulletPoints = variant && MODAL_SIGNUP_CONTENT[variant].bulletPoints;

  useEffect(() => {
    if (variant) {
      Track(AnalyticsEvents.Signup.ModalDisplayed, {
        variant,
        persona: "default",
      });
    }
  }, [variant]);

  /**
   * We need to track the modal dismissal event when the user clicks the close button.
   *
   * Creating this callback and passing it on to the Modal onClose property helps ignoring the track
   * when the `onClose` is triggered by the CTA.
   */
  const handleModalDismiss = useCallback(() => {
    Track(AnalyticsEvents.Signup.ModalDismissed, {
      variant,
      persona: "default",
    });
    onClose();
  }, [onClose, variant]);

  return (
    <Modal isOpen={!!variant} onClose={handleModalDismiss} variant="modern">
      <ModalContent
        p={["40px", "40px", "40px", "60px"]}
        pt={["60px", "60px", "60px", "80px"]}
        w="90vw"
        maxW={["500px", "500px", "780px", "900px", "1000px"]}
        borderWidth={0}
      >
        <Image
          pos="absolute"
          zIndex={-2}
          top={0}
          left={0}
          w="100%"
          h="100%"
          alt="Background"
          filter="blur(50px)"
          fit="cover"
          src={illustrationSrc}
        />
        <Box
          pos="absolute"
          zIndex={-1}
          top={0}
          left={0}
          w="100%"
          h="100%"
          opacity={0.25}
          bgColor="black"
        />
        <ModalBody>
          <IconButton
            pos="absolute"
            top="25px"
            right={["30px", "30px", "30px", "50px"]}
            variant="ghost"
            colorScheme="white"
            aria-label="Close"
            onClick={handleModalDismiss}
          >
            <Icon id="Ui/Cross" h="16px" />
          </IconButton>

          <Flex
            align="center"
            direction={["column-reverse", "column-reverse", "row"]}
            gap={["50px", "50px", "50px", "50px", "70px"]}
          >
            <VStack
              justify="space-between"
              w={["auto", "auto", "410px"]}
              spacing={10}
            >
              <VStack spacing={4}>
                <Text
                  fontSize={["28px", "28px", "28px", "32px", "38px"]}
                  size="title.xl"
                >
                  Sign Up For Free
                </Text>
                <Text
                  color="whiteAlpha.800"
                  fontSize={["20px", "20px", "20px", "22px", "24px"]}
                  size="body.2xl"
                >
                  {description}
                </Text>
              </VStack>

              <VStack align="flex-start" color="white" spacing={5}>
                {bulletPoints &&
                  bulletPoints?.map((bulletPoint, index) => (
                    <HStack
                      key={`bullet-point-${index}`}
                      align="center"
                      spacing={3}
                    >
                      <Center w="18px">
                        <Icon
                          id={bulletPoint.icon}
                          h={bulletPoint.size ?? "18px"}
                        />
                      </Center>
                      <Text size="body.lg">{bulletPoint.text}</Text>
                    </HStack>
                  ))}
              </VStack>

              <Button
                variant="primary"
                borderRadius="24px"
                colorScheme="white"
                size="lg"
                minW="250px"
                internalLink="/login"
                onClick={() => {
                  Track(AnalyticsEvents.Signup.ModalCTAClicked, {
                    variant,
                    persona: "default",
                  });
                  onClose();
                }}
              >
                Sign Up Now
              </Button>
            </VStack>

            <Image
              w={["250px", "250px", "300px", "350px", "400px"]}
              h={["250px", "250px", "300px", "350px", "400px"]}
              borderRadius="2xl"
              alt="illustration"
              fit="cover"
              src={illustrationSrc}
            />
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
